import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const C = {
  blue:     '#2563eb',
  blueDark: '#1d4ed8',
  dark:     '#0f172a',
  text:     '#1e293b',
  muted:    '#64748b',
  border:   '#e2e8f0',
  bg:       '#f8fafc',
  green:    '#16a34a',
  greenBg:  '#dcfce7',
};

const btn = (variant = 'primary', extra = {}) => ({
  padding: '12px 26px', borderRadius: 8, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', transition: 'all 0.15s',
  ...(variant === 'primary'
    ? { background: C.blue, color: 'white', border: 'none' }
    : { background: 'transparent', color: 'white', border: '1.5px solid rgba(255,255,255,0.35)' }),
  ...extra,
});

function useIsMobile(bp = 640) {
  const [m, setM] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= bp : false);
  useEffect(() => {
    const fn = () => setM(window.innerWidth <= bp);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return m;
}

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const FolderIcon   = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>;
const CalendarIcon = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const UsersIcon    = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const TaskIcon     = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
const DollarIcon   = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const LayersIcon   = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
const StarIcon     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const CheckIcon    = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const BellIcon     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;

// ── Navbar ──────────────────────────────────────────────────────────────────
function Navbar({ onIngresar, onComenzar }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const solidBg = scrolled || menuOpen;

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
      background: solidBg ? 'rgba(255,255,255,0.96)' : 'transparent',
      backdropFilter: solidBg ? 'blur(16px)' : 'none',
      borderBottom: solidBg ? `1px solid ${C.border}` : 'none',
      transition: 'all 0.25s',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', height: 64, display: 'flex', alignItems: 'center', padding: `0 ${isMobile ? 20 : 32}px`, gap: isMobile ? 0 : 32 }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <img src="/logo.svg" alt="Mi Estudio Juridico" width="32" height="32" style={{ objectFit: 'contain', flexShrink: 0 }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: solidBg ? C.dark : 'white', letterSpacing: -0.3 }}>Mi Estudio</span>
        </div>

        {/* Nav links — solo desktop */}
        {!isMobile && (
          <nav style={{ display: 'flex', gap: 28, marginLeft: 8 }}>
            {[['Funciones', '#funciones'], ['Precios', '#precios'], ['FAQ', '#faq']].map(([l, h]) => (
              <a key={l} href={h} style={{ fontSize: 14, color: scrolled ? C.muted : 'rgba(255,255,255,0.75)', textDecoration: 'none', fontWeight: 500, transition: 'color 0.15s' }}
                onMouseEnter={e => e.target.style.color = scrolled ? C.text : 'white'}
                onMouseLeave={e => e.target.style.color = scrolled ? C.muted : 'rgba(255,255,255,0.75)'}
              >{l}</a>
            ))}
          </nav>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isMobile && (
            <button onClick={onIngresar} style={{ background: 'none', border: `1.5px solid ${scrolled ? C.border : 'rgba(255,255,255,0.35)'}`, color: scrolled ? C.text : 'white', borderRadius: 8, padding: '7px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
              Ingresar
            </button>
          )}
          <button onClick={onComenzar} style={{ background: C.blue, color: 'white', border: 'none', borderRadius: 8, padding: isMobile ? '8px 14px' : '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Probar gratis
          </button>
          {isMobile && (
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px', color: solidBg ? C.text : 'white', fontSize: 22, lineHeight: 1, display: 'flex', alignItems: 'center' }}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          )}
        </div>
      </div>

      {/* Menú mobile desplegable */}
      {isMobile && menuOpen && (
        <div style={{ background: 'white', borderTop: `1px solid ${C.border}`, padding: '8px 20px 20px' }}>
          {[['Funciones', '#funciones'], ['Precios', '#precios'], ['FAQ', '#faq']].map(([l, h]) => (
            <a key={l} href={h} onClick={() => setMenuOpen(false)}
              style={{ display: 'block', padding: '13px 0', fontSize: 15, fontWeight: 600, color: C.text, textDecoration: 'none', borderBottom: `1px solid ${C.border}` }}
            >{l}</a>
          ))}
          <button onClick={() => { setMenuOpen(false); onIngresar(); }}
            style={{ display: 'block', width: '100%', marginTop: 16, padding: '12px', background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, fontWeight: 600, color: C.text, cursor: 'pointer' }}
          >
            Ingresar a mi cuenta
          </button>
        </div>
      )}
    </header>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────
function Hero({ onComenzar }) {
  const isMobile = useIsMobile();

  return (
    <section style={{
      background: `linear-gradient(140deg, #0a1628 0%, #0f2244 40%, #0f172a 100%)`,
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      padding: isMobile ? '100px 20px 60px' : '120px 32px 80px',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Decorative glows */}
      <div style={{ position: 'absolute', top: '15%', right: '8%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 65%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '5%', left: '3%', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.10) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)', backgroundSize: '48px 48px', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 40 : 64, position: 'relative' }}>
        {/* Columna izquierda */}
        <div style={{ flex: 1, maxWidth: isMobile ? '100%' : 560 }}>
          <h1 style={{ fontSize: isMobile ? 38 : 54, fontWeight: 800, color: 'white', lineHeight: 1.1, margin: '0 0 22px', letterSpacing: isMobile ? -1 : -1.8 }}>
            Tu estudio,<br/>
            <span style={{ color: '#60a5fa' }}>ordenado y siempre a mano.</span>
          </h1>

          <p style={{ fontSize: isMobile ? 16 : 18, color: 'rgba(255,255,255,0.62)', lineHeight: 1.75, margin: '0 0 18px', maxWidth: 490 }}>
            Mi Estudio centraliza tu agenda, clientes, trámites, expedientes y la contabilidad de tu estudio en una sola plataforma para que puedas llevar el orden y acceder a tu información en cualquier lugar.
          </p>

          {/* Mini trust indicators */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 20, marginBottom: 36, flexWrap: 'wrap' }}>
            {['Sin instalar nada', 'Recibí notificaciones en tu teléfono', 'Integrable con el calendario de tu teléfono'].map(t => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                {t}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 14, alignItems: isMobile ? 'stretch' : 'center' }}>
            <button onClick={onComenzar} style={btn('primary', { fontSize: 15, padding: '14px 34px', borderRadius: 9, textAlign: 'center' })}
              onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
              onMouseLeave={e => e.currentTarget.style.background = C.blue}
            >
              Probar gratis
            </button>
            <a href="#funciones" style={{ ...btn('ghost', { fontSize: 15, padding: '14px 26px', textDecoration: 'none', display: 'inline-block', textAlign: 'center' }) }}>
              Ver cómo funciona →
            </a>
          </div>
          <p style={{ marginTop: 16, fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>15 días gratis · Cancelás cuando querés</p>
        </div>

        {/* App preview — solo desktop */}
        {!isMobile && (
          <div style={{ flex: 1, maxWidth: 460, display: 'flex', justifyContent: 'center' }}>
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 18, padding: 24, width: '100%', backdropFilter: 'blur(12px)', boxShadow: '0 32px 80px rgba(0,0,0,0.4)' }}>
              {/* Window chrome */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                {['#ff5f57','#febc2e','#28c840'].map(c => <div key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 5, height: 20, marginLeft: 8, display: 'flex', alignItems: 'center', paddingLeft: 10 }}>
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>miestudiojuridico.com/app</span>
                </div>
              </div>

              {/* Alert card */}
              <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <BellIcon />
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>Vencimiento en 48 hs — García c/ López · Civil</span>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 130, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {['Panel','Expedientes','Agenda','Clientes','Contabilidad'].map((l, i) => (
                    <div key={l} style={{ padding: '7px 10px', borderRadius: 6, background: i === 0 ? 'rgba(37,99,235,0.45)' : 'transparent', color: i === 0 ? 'white' : 'rgba(255,255,255,0.38)', fontSize: 11, fontWeight: i === 0 ? 600 : 400, cursor: 'default' }}>{l}</div>
                  ))}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {[
                    ['García c/ López', 'Civil', '#ef4444', 'Audiencia hoy'],
                    ['Ramos s/ divorcio', 'Familia', '#f59e0b', 'Sin novedades'],
                    ['Torres c/ Municipio', 'Laboral', '#3b82f6', 'Pericia pdte.'],
                    ['Méndez c/ GCBA', 'Adm.', '#22c55e', 'Sentencia firme'],
                  ].map(([titulo, fuero, dot, estado]) => (
                    <div key={titulo} style={{ background: 'rgba(255,255,255,0.055)', borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                        <div style={{ width: 5, height: 5, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.88)' }}>{titulo}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ fontSize: 9, color: '#93c5fd', background: 'rgba(37,99,235,0.22)', borderRadius: 4, padding: '1px 5px' }}>{fuero}</span>
                        <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.38)' }}>{estado}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Social Proof Strip ────────────────────────────────────────────────────────
function SocialProof() {
  const isMobile = useIsMobile();
  const items = [
    { value: '15 min', label: 'Para empezar a usarlo' },
    { value: '0', label: 'Instalaciones requeridas' },
    { value: 'Multi-rol', label: 'Admin y colaboradores' },
    { value: '100%', label: 'Web · tablet · celular' },
  ];
  return (
    <div style={{ background: C.blue, padding: isMobile ? '24px 20px' : '30px 32px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 20 : 0 }}>
        {items.map(({ value, label }) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: 'white', letterSpacing: -0.5 }}>{value}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 3, fontWeight: 500 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Features ─────────────────────────────────────────────────────────────────
function Features() {
  const isMobile = useIsMobile();
  const features = [
    {
      Icon: UsersIcon,
      color: '#2563eb', bg: '#eff6ff',
      title: 'Clientes centralizados',
      tagline: 'Toda la información de cada cliente',
      desc: 'Ficha completa con datos de contacto, historial de causas vinculadas y seguimiento de cada relación profesional. Todo en un solo lugar, sin buscar en correos ni cuadernos.',
    },
    {
      Icon: FolderIcon,
      color: '#7c3aed', bg: '#f5f3ff',
      title: 'Expedientes organizados',
      tagline: 'Cada causa, vinculada a su cliente',
      desc: 'Seguimiento completo de cada causa con estados, etapas procesales y notas. Cada expediente se vincula automáticamente al cliente y las tareas correspondientes.',
    },
    {
      Icon: LayersIcon,
      color: '#0891b2', bg: '#ecfeff',
      title: 'Todo conectado',
      tagline: 'Clientes → expedientes → tareas, en red',
      desc: 'Cada cliente tiene sus expedientes. Cada expediente tiene sus tareas. Todo se relaciona entre sí para que nunca pierdas el hilo de ningún asunto.',
    },
    {
      Icon: TaskIcon,
      color: '#059669', bg: '#ecfdf5',
      title: 'Tareas y pendientes',
      tagline: 'Delegá y controlá en tiempo real',
      desc: 'Asigná tareas a tu equipo, establecé prioridades y seguí el avance desde el panel. Consultá tus pendientes desde el celular en cualquier momento.',
    },
    {
      Icon: DollarIcon,
      color: '#d97706', bg: '#fffbeb',
      title: 'Contabilidad del estudio',
      tagline: 'Sabé exactamente cuánto ganás',
      desc: 'Registrá honorarios, gastos e ingresos por causa. Controlá la salud financiera de tu estudio en tiempo real, sin planillas ni cuadernos.',
    },
    {
      Icon: CalendarIcon,
      color: '#dc2626', bg: '#fef2f2',
      title: 'Agenda y Google Calendar',
      tagline: 'Nunca más perdés una fecha',
      desc: 'Gestioná audiencias y vencimientos con alertas automáticas. Próximamente: sincronización con Google Calendar para tener todo en un solo calendario.',
    },
  ];

  return (
    <section id="funciones" style={{ padding: isMobile ? '60px 20px' : '96px 32px', background: 'white' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 64 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Gestor Jurídico Integral</div>
          <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: C.dark, margin: '0 0 16px', letterSpacing: -1 }}>Todo lo que tu estudio necesita</h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: C.muted, maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
            Sistema de gestión integral de estudios jurídicos, diseñado para poder llevar el control centralizado de tu estudio desde cualquier parte.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(310px, 1fr))', gap: 16 }}>
          {features.map(({ Icon, color, bg, title, tagline, desc }) => (
            <div key={title} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 16, padding: isMobile ? '22px 20px' : '28px 28px', transition: 'box-shadow 0.2s, transform 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
            >
              <div style={{ width: 46, height: 46, background: bg, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color, marginBottom: 18 }}>
                <Icon />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>{tagline}</div>
              <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.65 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── How it works ──────────────────────────────────────────────────────────────
function HowItWorks({ onComenzar }) {
  const isMobile = useIsMobile();
  const steps = [
    {
      n: '1', title: 'Creá tu estudio',
      desc: 'Registrate con tu email. En menos de 15 minutos tenés el estudio configurado, los roles asignados y listo para operar.',
    },
    {
      n: '2', title: 'Cargá tu práctica',
      desc: 'Ingresá tus clientes, expedientes y agenda. La interfaz es intuitiva: si sabés usar el correo, sabés usar Mi Estudio.',
    },
    {
      n: '3', title: 'Trabajá desde donde estés',
      desc: 'Accedé desde la computadora, tablet o celular. Vos y tu equipo, siempre con la misma información actualizada.',
    },
  ];

  return (
    <section style={{ padding: isMobile ? '60px 20px' : '96px 32px', background: C.bg }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 64 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Cómo funciona</div>
          <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: C.dark, margin: '0 0 16px', letterSpacing: -1 }}>Empezá en minutos, no en semanas</h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: C.muted, maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>
            Sin onboarding complicado, sin migración de datos dolorosa, sin soporte técnico requerido.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: isMobile ? 32 : 40 }}>
          {steps.map((s) => (
            <div key={s.n} style={{ position: 'relative', textAlign: 'center' }}>
              <div style={{ width: 54, height: 54, borderRadius: '50%', background: `linear-gradient(135deg, ${C.blue}, #1d4ed8)`, color: 'white', fontSize: 22, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px', boxShadow: '0 4px 16px rgba(37,99,235,0.35)' }}>{s.n}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 10 }}>{s.title}</div>
              <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, maxWidth: 260, margin: '0 auto' }}>{s.desc}</div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 56 }}>
          <button onClick={onComenzar} style={btn('primary', { fontSize: 15, padding: '14px 38px', borderRadius: 9, width: isMobile ? '100%' : 'auto' })}
            onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
            onMouseLeave={e => e.currentTarget.style.background = C.blue}
          >
            Comenzar ahora — es gratis
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Testimonials ──────────────────────────────────────────────────────────────
function Testimonials() {
  const isMobile = useIsMobile();
  const testimonials = [
    {
      quote: 'Tenía los expedientes en papel, en el correo y en una hoja de Excel. Ahora todo está en un lugar y no volví a perder una audiencia. En serio.',
      name: 'Carla Mendizábal',
      role: 'Abogada independiente · Buenos Aires',
      initials: 'CM',
      avatarColor: '#6d28d9',
    },
    {
      quote: 'Implementamos Mi Estudio con todo el equipo en una tarde. La asignación de tareas cambió completamente cómo organizamos el trabajo interno.',
      name: 'Estudio Ferreyra & Asociados',
      role: 'Derecho comercial y laboral · Córdoba',
      initials: 'FA',
      avatarColor: '#0891b2',
    },
    {
      quote: 'Lo que más me sorprendió fue lo simple que es. En una semana usaba todas las funciones sin necesitar soporte. Y el acceso desde el celular es perfecto.',
      name: 'Pablo Ochoa',
      role: 'Abogado · Bogotá, Colombia',
      initials: 'PO',
      avatarColor: '#059669',
    },
  ];

  return (
    <section style={{ padding: isMobile ? '60px 20px' : '96px 32px', background: 'white' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 56 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Testimonios</div>
          <h2 style={{ fontSize: isMobile ? 26 : 40, fontWeight: 800, color: C.dark, margin: 0, letterSpacing: -1 }}>Lo que dicen los estudios que ya lo usan</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          {testimonials.map(({ quote, name, role, initials, avatarColor }) => (
            <div key={name} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 16, padding: isMobile ? '22px 20px' : '28px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'flex', gap: 3 }}>
                {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
              </div>
              <p style={{ fontSize: 15, color: C.text, lineHeight: 1.7, margin: 0, flex: 1 }}>
                "{quote}"
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                  {initials}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{name}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────────
function Pricing({ onComenzar }) {
  const isMobile = useIsMobile();
  const features = [
    '1 estudio, 1 colaborador incluido',
    'Expedientes y trámites extrajudiciales ilimitados',
    'Agenda con alertas de vencimientos',
    'Gestión de clientes',
    'Sistema de asignación de tareas',
    'Contabilidad de tu estudio',
  ];

  return (
    <section id="precios" style={{ padding: isMobile ? '60px 20px' : '96px 32px', background: C.bg }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 56 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Precios</div>
          <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: C.dark, margin: '0 0 14px', letterSpacing: -1 }}>Un solo precio, sin letra chica</h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: C.muted, margin: 0 }}>15 días de prueba sin límites. Cancelás cuando querés.</p>
        </div>

        <div style={{ maxWidth: 420, margin: '0 auto' }}>
          <div style={{
            border: `2px solid ${C.blue}`,
            borderRadius: 18, padding: isMobile ? '28px 20px' : '36px 32px',
            background: 'white',
            boxShadow: '0 8px 40px rgba(37,99,235,0.12)',
          }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: C.text, marginBottom: 6 }}>Mi Estudio</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 24, lineHeight: 1.5 }}>Todo lo que necesitás para llevar tu estudio.</div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
              <span style={{ fontSize: isMobile ? 40 : 48, fontWeight: 800, color: C.dark, letterSpacing: -1 }}>USD 10</span>
              <span style={{ fontSize: 14, color: C.muted }}>/mes</span>
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 28 }}>Facturación mensual · Precios en dólares</div>

            <button onClick={onComenzar} style={{
              width: '100%', padding: '13px', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              background: C.blue, color: 'white', border: `2px solid ${C.blue}`,
              marginBottom: 28, transition: 'all 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1d4ed8'; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.blue; }}
            >
              Probar gratis
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {features.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: C.text }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CheckIcon />
                  </div>
                  {f}
                </div>
              ))}
            </div>
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: 28, fontSize: 13, color: C.muted }}>
          ¿Necesitás más de un colaborador o varios estudios? <a href="mailto:hola@miestudiojuridico.com" style={{ color: C.blue, textDecoration: 'none', fontWeight: 600 }}>Escribinos y lo hablamos.</a>
        </p>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  const isMobile = useIsMobile();
  const items = [
    [
      '¿Puedo usar Mi Estudio desde el celular?',
      'Sí. La plataforma es completamente responsiva y funciona en cualquier dispositivo con navegador web: computadora, tablet o celular. No hay app para descargar ni nada que instalar.',
    ],
    [
      '¿Qué tan segura es mi información?',
      'Muy segura. Todos los datos se almacenan en la nube con cifrado en tránsito y en reposo. Los backups son automáticos. Nunca compartimos ni vendemos información de tus clientes o expedientes a terceros.',
    ],
    [
      '¿Qué pasa con mis datos si cancelo?',
      'Tus datos son siempre tuyos. Si decidís cancelar, podés exportar toda tu información antes de cerrar la cuenta. No hay lock-in ni penalidades.',
    ],
    [
      '¿Puedo tener varios estudios en una cuenta?',
      'Sí, desde el plan Profesional podés manejar múltiples estudios con datos y equipos separados. Cambiás entre ellos con un clic desde el menú lateral.',
    ],
    [
      '¿Cómo funciona el sistema de roles?',
      'Hay dos roles: Admin y Colaborador. El admin gestiona el estudio completo (expedientes, clientes, contabilidad, configuración). Los colaboradores acceden solo a las secciones habilitadas, sin ver contabilidad ni configuración sensible.',
    ],
    [
      '¿Puedo invitar a mi equipo?',
      'Sí. El admin puede invitar colaboradores por email. Cada uno recibe un enlace para crear su contraseña y acceder al estudio. Podés agregar o quitar colaboradores en cualquier momento.',
    ],
    [
      '¿El precio está en dólares?',
      'Sí, los precios están en USD para mantener consistencia en toda la región. El cobro se realiza a través de Mercado Pago, disponible en Argentina, Chile, Colombia, México, Uruguay y más países.',
    ],
  ];

  return (
    <section id="faq" style={{ padding: isMobile ? '60px 20px' : '96px 32px', background: 'white' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 36 : 56 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>FAQ</div>
          <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: C.dark, margin: 0, letterSpacing: -1 }}>Preguntas frecuentes</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {items.map(([q, a], i) => (
            <div key={q} style={{ background: C.bg, border: `1px solid ${open === i ? C.blue : C.border}`, borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s' }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: '100%', background: 'none', border: 'none', padding: isMobile ? '16px 16px' : '18px 22px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer', textAlign: 'left', gap: 16,
              }}>
                <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: open === i ? C.blue : C.text, transition: 'color 0.15s' }}>{q}</span>
                <span style={{ fontSize: 20, color: open === i ? C.blue : C.muted, flexShrink: 0, transition: 'transform 0.2s, color 0.15s', transform: open === i ? 'rotate(45deg)' : 'none', display: 'block', lineHeight: 1 }}>+</span>
              </button>
              {open === i && (
                <div style={{ padding: isMobile ? '0 16px 16px' : '0 22px 20px', fontSize: 14, color: C.muted, lineHeight: 1.75 }}>{a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA Final ─────────────────────────────────────────────────────────────────
function CTAFinal({ onComenzar }) {
  const isMobile = useIsMobile();
  return (
    <section style={{ background: `linear-gradient(140deg, #0a1628 0%, #0f2244 50%, #0f172a 100%)`, padding: isMobile ? '72px 20px' : '100px 32px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.2) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ maxWidth: 580, margin: '0 auto', position: 'relative' }}>
        <h2 style={{ fontSize: isMobile ? 30 : 44, fontWeight: 800, color: 'white', margin: '0 0 18px', letterSpacing: isMobile ? -0.5 : -1.2, lineHeight: 1.15 }}>
          Tu próxima audiencia ya<br/>debería estar en el sistema.
        </h2>
        <p style={{ fontSize: isMobile ? 16 : 18, color: 'rgba(255,255,255,0.6)', margin: '0 0 42px', lineHeight: 1.65 }}>
          15 días de prueba gratis.<br/>Si no es para vos, cancelás en un clic.
        </p>
        <button onClick={onComenzar} style={btn('primary', { fontSize: isMobile ? 15 : 16, padding: isMobile ? '14px 32px' : '15px 42px', borderRadius: 10, boxShadow: '0 4px 20px rgba(37,99,235,0.4)', width: isMobile ? '100%' : 'auto' })}
          onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'}
          onMouseLeave={e => e.currentTarget.style.background = C.blue}
        >
          Crear mi estudio gratis
        </button>
        <p style={{ marginTop: 20, fontSize: 12, color: 'rgba(255,255,255,0.28)' }}>
          Disponible para estudios jurídicos en toda Latinoamérica · Precios en USD
        </p>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer({ onIngresar }) {
  const isMobile = useIsMobile();
  return (
    <footer style={{ background: C.dark, padding: isMobile ? '28px 20px' : '36px 32px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 16 : 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/logo.svg" alt="Mi Estudio Juridico" width="28" height="28" style={{ objectFit: 'contain' }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>Mi Estudio</span>
        </div>

        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.28)', order: isMobile ? 1 : 0 }}>
          © {new Date().getFullYear()} Mi Estudio Jurídico · miestudiojuridico.com
        </div>

        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <a href="mailto:hola@miestudiojuridico.com" style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', textDecoration: 'none', transition: 'color 0.15s' }}
            onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.7)'}
            onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.38)'}
          >Contacto</a>
          <button onClick={onIngresar} style={{ background: 'none', border: 'none', fontSize: 12, color: 'rgba(255,255,255,0.38)', cursor: 'pointer', fontWeight: 500, transition: 'color 0.15s', padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.38)'}
          >
            Ingresar →
          </button>
        </div>
      </div>
    </footer>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate();
  const goApp      = () => navigate('/app');
  const goRegistro = () => navigate('/registro');

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', overflowX: 'hidden' }}>
      <Navbar    onIngresar={goApp} onComenzar={goRegistro} />
      <Hero      onComenzar={goRegistro} />
      <SocialProof />
      <Features />
      <HowItWorks onComenzar={goRegistro} />
      <Testimonials />
      <Pricing   onComenzar={goRegistro} />
      <FAQ />
      <CTAFinal  onComenzar={goRegistro} />
      <Footer    onIngresar={goApp} />
    </div>
  );
}
